import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sendCachedRequest } from '../utils/apiCache';
import { BASE_URL } from '../URL/constants';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import getCurrentUser from "./common/GetCurrentUser";

const DOMPURIFY_CONFIG = {
  ADD_TAGS: [
    'style',
    'svg',
    'path',
    'circle',
    'rect',
    'line',
    'polyline',
    'polygon',
    'g',
    'defs'
  ],
  ADD_ATTR: [
    'style',
    'class',
    'id',
    'data-company-name',
    'data-company-name-title',
    'data-current-year',
    'fill',
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'viewBox',
    'd',
    'points',
    'x1',
    'x2',
    'y1',
    'y2',
    'x',
    'y',
    'rx',
    'ry',
    'cx',
    'cy',
    'width',
    'height'
  ],
  FORBID_TAGS: ['script']
};

const SHADOW_WRAPPER_CLASS = 'dynamic-privacy-policy-shadow-root';

const hoistHeadStyles = (doc) => {
  if (!doc.head) return;
  const headStyles = Array.from(doc.head.querySelectorAll('style'));
  if (!headStyles.length) return;

  headStyles.reverse().forEach((styleNode) => {
    const clone = styleNode.cloneNode(true);
    doc.body.insertBefore(clone, doc.body.firstChild);
  });
};

const adaptStylesForShadow = (doc) => {
  doc.querySelectorAll('style').forEach((styleTag) => {
    const content = styleTag.textContent;
    if (!content) return;
    styleTag.textContent = content
      .replace(/\.privacy-policy-wrapper/g, `:host`)
      .replace(/\.privacy-policy-wrapper\s+/g, `:host `)
      .replace(/\bhtml\b/g, `.${SHADOW_WRAPPER_CLASS}`)
      .replace(/\bbody\b/g, `.${SHADOW_WRAPPER_CLASS}`);
  });
};

const wrapContentForShadow = (doc) => {
  if (!doc?.body) return;
  const wrapper = doc.createElement('div');
  wrapper.className = SHADOW_WRAPPER_CLASS;
  wrapper.innerHTML = doc.body.innerHTML;
  doc.body.innerHTML = '';
  doc.body.appendChild(wrapper);
};

const replaceFallbackName = (doc, companyName) => {
  if (!doc || !companyName) return;
  const FALLBACK = 'Looks Salon';
  if (FALLBACK.toLowerCase() === companyName.toLowerCase()) return;

  doc.querySelectorAll('[data-company-name]').forEach((node) => {
    if (node.textContent?.trim() === FALLBACK) {
      node.textContent = companyName;
    }
  });

  doc.querySelectorAll('*').forEach((node) => {
    if (node.childNodes?.length) {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE && child.textContent?.includes(FALLBACK)) {
          child.textContent = child.textContent.replace(new RegExp(FALLBACK, 'g'), companyName);
        }
      });
    }
  });
};

const applyCompanyName = (doc, companyName) => {
  if (!doc || !companyName) return;

  doc.querySelectorAll('[data-company-name]').forEach((node) => {
    node.textContent = companyName;
  });

  const titleNode = doc.querySelector('[data-company-name-title]');
  if (titleNode) {
    titleNode.textContent = `${companyName} - Privacy Policy`;
  }

  replaceFallbackName(doc, companyName);
};

const markCurrentYearPlaceholders = (doc) => {
  doc.querySelectorAll('script').forEach((scriptNode) => {
    const content = scriptNode.textContent || '';
    if (/new Date\(\)/.test(content)) {
      const span = doc.createElement('span');
      span.setAttribute('data-current-year', '');
      scriptNode.replaceWith(span);
    } else {
      scriptNode.remove();
    }
  });
};

const preparePrivacyPolicyDocument = (doc) => {
  hoistHeadStyles(doc);
  wrapContentForShadow(doc);
  adaptStylesForShadow(doc);
  markCurrentYearPlaceholders(doc);
};

const PrivacyPolicy = () => {
  const [privacyHtml, setPrivacyHtml] = useState('');
  const { currentUser } = getCurrentUser();
  const shadowHostRef = useRef(null);
  const shadowRootRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrivacyData = async () => {
      try {
        const response = await sendCachedRequest(
          'POST',
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: 'public_site_info' }
        );
        
        const siteInfo = response.data.data.public_site_info;
        const companyInfo = response.data.data.company_info;
        const companyName = companyInfo?.name?.trim() || 'Looks Salon';

        // Get privacy policy content from API
        const content = siteInfo?.privacy_policy_content?.body || 
                        '<div class="privacy-policy-wrapper"><div class="privacy-policy"><h1>Privacy Policy</h1><p>Privacy policy content will be available soon.</p></div></div>';
        
        // Parse and update company name in the content
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        
        applyCompanyName(doc, companyName);
        preparePrivacyPolicyDocument(doc);
        
        setPrivacyHtml(doc.body.innerHTML);
      } catch (err) {
        console.error('Error fetching privacy policy:', err);
        setPrivacyHtml('');
      }
    };

    fetchPrivacyData();
  }, [currentUser]);

  const ensureShadowRoot = useCallback(() => {
    if (shadowRootRef.current) return shadowRootRef.current;
    if (!shadowHostRef.current) return null;
    shadowRootRef.current = shadowHostRef.current.attachShadow({ mode: 'open' });
    return shadowRootRef.current;
  }, []);

  const sanitizedPrivacyHtml = useMemo(() => {
    if (!privacyHtml) return '';
    return DOMPurify.sanitize(privacyHtml, DOMPURIFY_CONFIG);
  }, [privacyHtml]);

  useEffect(() => {
    const shadowRoot = ensureShadowRoot();
    if (!shadowRoot) return;

    if (!sanitizedPrivacyHtml) {
      shadowRoot.innerHTML = '';
      return;
    }

    // Inject CSS to ensure proper styling
    const baseStyles = `
      <style>
        :host {
          display: block;
          width: 100%;
          position: relative;
        }
        .${SHADOW_WRAPPER_CLASS} {
          width: 100%;
          min-height: 100vh;
        }
      </style>
    `;

    shadowRoot.innerHTML = baseStyles + sanitizedPrivacyHtml;

    // Update year and date dynamically
    const yearValue = new Date().getFullYear().toString();
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    shadowRoot.querySelectorAll('[data-current-year]').forEach((node) => {
      // Check if it's in last-updated section, use full date, otherwise just year
      if (node.closest('.last-updated')) {
        node.textContent = currentDate;
      } else {
        node.textContent = yearValue;
      }
    });

    // Handle link clicks for React Router navigation
    const handleLinkClick = (event) => {
      const anchor = event.target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Check if it's an external link or special link
      const isExternal = /^(https?:|mailto:|tel:|\/\/)/.test(href);
      const isAnchor = href.startsWith('#');
      const isMailto = href.startsWith('mailto:');
      const isTel = href.startsWith('tel:');

      // Allow default behavior for external links, anchors, mailto, and tel
      if (isExternal || isAnchor || isMailto || isTel) {
        return; // Allow default behavior
      }

      // Internal link - prevent default and use React Router
      event.preventDefault();
      event.stopPropagation();

      // Normalize the path (ensure it starts with /)
      const path = href.startsWith('/') ? href : `/${href}`;
      
      // Navigate using React Router
      navigate(path);
    };

    shadowRoot.addEventListener('click', handleLinkClick);

    return () => {
      shadowRoot.removeEventListener('click', handleLinkClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedPrivacyHtml, ensureShadowRoot]);

  if (!privacyHtml) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading Privacy Policy...</div>;
  }

  return (
    <div
      className="dynamic-privacy-policy"
      ref={shadowHostRef}
    />
  );
};

export default PrivacyPolicy;
